import type { FC } from "react"

import type {
  ConsumptionInsightsReason,
  ConsumptionInsightsUnavailableEvidenceDto,
  UsagePatternsViewDto,
} from "~/api/dto/proposal-consumption.dto"

import { HourDayHeatmap } from "./HourDayHeatmap"
import {
  insufficientEvidenceCopy,
  TYPICAL_DAY_EMPTY_COPY,
} from "./TypicalDayTab"

export const USAGE_PATTERNS_GRID_IMPORT_COPY = `This profile reflects grid import rather than native household load, so Usage patterns is not available. Other insights may still be available when their evidence qualifies.`

export const USAGE_PATTERNS_INSUFFICIENT_DAYS_COPY = `Green Button data is connected, but fewer than seven complete local days are available. Usage patterns needs more observed days before it can be shown.`

const formatCoverageDate = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
    timeZone: `UTC`,
  }).format(date)
}

export const formatUsagePatternsBasisCopy = (
  viewModel: UsagePatternsViewDto,
): string => {
  const channelLabel =
    viewModel.measurementBasis === `GRID_IMPORT`
      ? `grid import`
      : viewModel.channelBasis === `netKwh`
        ? `net usage`
        : `import consumption`
  const statusLabel =
    viewModel.evidenceStatus === `complete` ? `Complete` : `Partial`
  return `${statusLabel} ${channelLabel} · ${viewModel.observedDayCount} observed days · ${viewModel.coveragePercent}% of a year · ${formatCoverageDate(viewModel.coverageStart)} – ${formatCoverageDate(viewModel.coverageEnd)}`
}

export const UsagePatternsEmptyState: FC<{
  reason?: ConsumptionInsightsReason
  evidence?: ConsumptionInsightsUnavailableEvidenceDto
}> = ({ evidence, reason }) => {
  const copy =
    reason === `grid_import_basis`
      ? USAGE_PATTERNS_GRID_IMPORT_COPY
      : reason === `insufficient_observed_days`
        ? insufficientEvidenceCopy(
            `Usage patterns`,
            evidence,
            USAGE_PATTERNS_INSUFFICIENT_DAYS_COPY,
          )
        : TYPICAL_DAY_EMPTY_COPY

  return (
    <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
      <p className="mx-auto max-w-lg font-display text-base font-semibold text-foreground">
        {copy}
      </p>
    </div>
  )
}

export const UsagePatternsTab: FC<{ viewModel: UsagePatternsViewDto }> = ({
  viewModel,
}) => {
  const isPartial = viewModel.evidenceStatus === `partial`
  const periodLabel = isPartial ? `observed period` : `observed year`

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-sm font-semibold text-foreground">
          Usage patterns
        </h3>
        <p className="mt-1 text-sm text-muted">
          {formatUsagePatternsBasisCopy(viewModel)}
        </p>
        <p className="mt-1 text-sm text-muted">
          Hour-by-day intensity across the {periodLabel}. Gaps read as No data;
          imputed days carry gold markers.
        </p>
        {viewModel.measurementBasis === `GRID_IMPORT` ? (
          <p className="mt-1 text-sm text-muted">
            Electricity drawn from the grid. Solar energy used directly in the
            home is not included.
          </p>
        ) : null}
        {viewModel.imputedDayIndexes.length > 0 ? (
          <p className="mt-2 font-display text-xs font-semibold text-muted">
            Partly imputed
          </p>
        ) : null}
      </div>
      <HourDayHeatmap viewModel={viewModel} />
    </div>
  )
}
